import { DEFAULT_QUIZ_RULES_HTML, emptyQuizDraft } from "./quiz/quizDraftDefaults";

/** Microlab brand accent used by the persistent AI assistant. */
export const AI_ASSISTANT_BLUE = "#0061FE";

/** Desktop panel width as a fraction of the viewport. */
export const AI_PANEL_WIDTH_RATIO = 0.33;

/** Approximate panel width used for layout margin (matches CSS). */
export const AI_PANEL_WIDTH_CSS = "min(33vw, 480px)";

/** sessionStorage key prefix — always suffixed with authenticated user id. */
export const AI_STORAGE_PREFIX = "microlab_ai_assistant_v1";

export const MESSAGE_STATUS = {
  PENDING: "pending",
  SENT: "sent",
  FAILED: "failed",
  STREAMING: "streaming",
};

export const RESULTS_OPTION_CHIPS = [
  { id: "open-results", label: "Open Results page", prompt: "__open_results__" },
  {
    id: "retry-results",
    label: "Refresh analysis",
    prompt: "Analyze my student quiz results again",
  },
  {
    id: "list-students",
    label: "List students",
    prompt: "Show students in my authorized scope",
  },
];

export const USERS_OPTION_CHIPS = [
  {
    id: "list-students",
    label: "List students",
    prompt: "Show students in my authorized scope",
  },
  {
    id: "show-marks",
    label: "Show marks",
    prompt: "Show student marks and quiz scores",
  },
  { id: "open-results", label: "Open Results page", prompt: "__open_results__" },
];

/** Build a chat reply from a university/class user list (offline directory path). */
export function formatUsersDirectory(users, { universityWide = false } = {}) {
  const list = Array.isArray(users) ? users : [];
  if (!list.length) {
    return {
      reply: universityWide
        ? "There are no matching users in your university yet."
        : "There are no students linked to your authorized classes yet. Once students are assigned or submit a quiz, I can list their details here.",
      options: USERS_OPTION_CHIPS,
    };
  }
  const lines = list
    .slice(0, 25)
    .map((u) => {
      const name =
        [u.first_name, u.last_name].filter(Boolean).join(" ") ||
        u.name ||
        "—";
      return `- ${name} · ${u.email || "—"} · ${u.role || "student"}`;
    })
    .join("\n");
  return {
    reply: [
      universityWide
        ? `Here are users in your university (${list.length}).`
        : `Here are students from your authorized scope (${list.length}).`,
      lines,
      list.length > 25 ? `(Showing 25 of ${list.length}.)` : null,
    ]
      .filter(Boolean)
      .join("\n"),
    options: USERS_OPTION_CHIPS,
  };
}

/** Build a chat reply from GET /quiz/results (offline / direct analysis path). */
export function formatQuizResultsAnalysis(payload) {
  const body = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const results = Array.isArray(body?.results) ? body.results : [];
  const total = Number(body?.total ?? results.length) || 0;

  if (!total || results.length === 0) {
    return {
      reply:
        "There are no submitted quiz attempts for your authorized classes yet. Once students complete a quiz, I can summarize scores, averages, and who needs attention.",
      options: RESULTS_OPTION_CHIPS,
    };
  }

  const scores = results
    .map((r) => Number(r.score))
    .filter((n) => Number.isFinite(n));
  const avg =
    scores.length > 0
      ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
      : null;
  const sorted = [...results].sort(
    (a, b) => (Number(b.score) || 0) - (Number(a.score) || 0)
  );
  const top = sorted[0];
  const low = sorted[sorted.length - 1];
  const lines = results
    .slice(0, 12)
    .map((r) => {
      const when = r.submitted_at
        ? new Date(r.submitted_at).toLocaleString()
        : "";
      return `- ${r.student_name || "Student"} · ${r.quiz_title || "Quiz"} · score ${
        r.score ?? "—"
      }${r.correct_count != null && r.total_questions != null ? ` (${r.correct_count}/${r.total_questions})` : ""}${
        when ? ` · ${when}` : ""
      }`;
    })
    .join("\n");

  return {
    reply: [
      `Here’s an analysis of ${total} submitted attempt${total === 1 ? "" : "s"} in your authorized classes.`,
      avg != null ? `Average score: ${avg}.` : null,
      top && low && top !== low
        ? `Highest: ${top.student_name || "Student"} (${top.score ?? "—"}) · Lowest: ${
            low.student_name || "Student"
          } (${low.score ?? "—"}).`
        : null,
      lines,
      total > 12
        ? `(Showing 12 of ${total}. Open the Results page for the full list and filters.)`
        : null,
    ]
      .filter(Boolean)
      .join("\n"),
    options: RESULTS_OPTION_CHIPS,
  };
}

/** Starter titles shown in a blank chat. */
export const STARTER_ACTIONS = [
  {
    id: "create-quiz",
    label: "Create a quiz",
    description: "Build a quiz from Microlab or your private bank",
    prompt: "Create a quiz",
    comingSoon: false,
  },
  {
    id: "check-quiz",
    label: "Check quiz results",
    description: "See scores from your assigned classes",
    prompt: "Show the results of students in my classes",
    comingSoon: false,
  },
  {
    id: "list-users",
    label: "List students / users",
    description: "User details in your authorized scope",
    prompt: "Show students in my authorized scope",
    comingSoon: false,
  },
  {
    id: "ask-quizzes",
    label: "Ask about my quizzes",
    description: "Questions about titles, banks, and setup",
    prompt: "Help me with my quizzes and question bank",
    comingSoon: false,
  },
  {
    id: "make-presentation",
    label: "Make a presentation",
    description: "Coming soon",
    prompt: "Make a presentation",
    comingSoon: true,
  },
];

export const QUICK_ACTIONS = STARTER_ACTIONS.filter((a) => !a.comingSoon).map((a) => ({
  id: a.id,
  label: a.label,
  prompt: a.prompt,
}));

export const QUIZ_QUICK_ACTIONS = [
  {
    id: "create-quiz",
    label: "Create a quiz",
    prompt: "Create a quiz",
  },
  {
    id: "histology-quiz",
    label: "Histology · 20 medium · 30 min",
    prompt:
      "Create a histology quiz using 20 medium questions from Microlab's public bank, with a 30-minute time limit.",
  },
  {
    id: "check-quiz",
    label: "Check quiz results",
    prompt: "Show the results of students in my classes",
  },
];

/**
 * Parse a complete “create a … quiz using N …” instruction into a local draft
 * so the chat stays useful when Quiz Copilot is offline.
 */
export function parseLocalQuizCreatePrompt(text) {
  const raw = String(text || "").trim();
  const t = raw.toLowerCase();
  if (!/\bquiz\b/.test(t) || !/\bcreate\b/.test(t)) return null;

  const catalogueMatch = t.match(/\b(histology|embryology|anatomy)\b/);
  if (!catalogueMatch) return null;

  const countMatch =
    t.match(/\b(\d+)\s*(?:medium|easy|hard)?\s*questions?\b/) ||
    t.match(/\busing\s+(\d+)\b/);
  const minutesMatch = t.match(/\b(\d+)\s*[- ]?\s*min(?:ute)?s?\b/);
  const difficultyMatch = t.match(/\b(easy|medium|hard)\b/);
  const privateBank = /private/.test(t);

  const catalogue = catalogueMatch[1];
  const name = catalogue[0].toUpperCase() + catalogue.slice(1);
  const numQuestions = Math.min(100, Math.max(1, Number(countMatch?.[1] || 20)));
  const timeLimit = Math.min(180, Math.max(5, Number(minutesMatch?.[1] || 30)));
  const difficulty = difficultyMatch?.[1] || "medium";
  const bankSource = privateBank ? "private" : "microlab";

  const draft = {
    ...emptyQuizDraft(),
    title: `${name} Quiz`,
    time_limit_minutes: timeLimit,
    rules_html: DEFAULT_QUIZ_RULES_HTML,
    bank_preference: bankSource,
    awaiting_confirmation: true,
    topic_rules: [
      {
        professor_topic_id: "",
        bank_source: bankSource,
        catalogue: bankSource === "microlab" ? catalogue : "",
        quiz_title_ids: [],
        topic_label: name,
        num_questions: numQuestions,
        difficulty_filter: difficulty,
      },
    ],
  };

  const confirmation = {
    title: draft.title,
    time_limit_minutes: timeLimit,
    total_questions: numQuestions,
    topic_rules: [
      {
        source: bankSource,
        catalogue: bankSource === "microlab" ? catalogue : null,
        topic_label: name,
        subtitles: ["All"],
        difficulty,
        num_questions: numQuestions,
      },
    ],
    instructions_preview: String(DEFAULT_QUIZ_RULES_HTML)
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 240),
  };

  return {
    reply: `Here’s a draft ${name} quiz (${numQuestions} ${difficulty} questions, ${timeLimit} min). Review it below — confirm to open the Create Quiz form with these settings, or pick another option.`,
    options: [
      { id: "create-quiz", label: "Start over", prompt: "Create a quiz" },
      {
        id: "preset-alt",
        label: "10 questions · 20 min",
        prompt: `Create a ${catalogue} quiz using 10 medium questions from Microlab's public bank, with a 20-minute time limit.`,
      },
    ],
    draft,
    confirmation,
  };
}

/** Local replies when the quiz-copilot API is unavailable or for coming-soon actions. */
export function getLocalAssistantFallback(text) {
  const raw = String(text || "").trim();
  const t = raw.toLowerCase();

  if (/make a presentation|create a presentation|^presentation$/.test(t)) {
    return {
      reply:
        "Presentation creation is coming soon. For now I can help you create quizzes, check quiz results, or answer questions about your quizzes.",
      options: [
        { id: "create-quiz", label: "Create a quiz", prompt: "Create a quiz" },
        { id: "check-quiz", label: "Check quiz results", prompt: "Show the results of students in my classes" },
        { id: "ask-quizzes", label: "Ask about my quizzes", prompt: "Help me with my quizzes and question bank" },
      ],
    };
  }

  if (/^create a quiz\.?$/.test(t) || t === "create quiz") {
    return {
      reply: "What topic would you like your quiz to cover?",
      options: [
        { id: "cat:histology", label: "Histology (Microlab)", prompt: "cat:histology" },
        { id: "cat:embryology", label: "Embryology (Microlab)", prompt: "cat:embryology" },
        { id: "cat:anatomy", label: "Anatomy (Microlab)", prompt: "cat:anatomy" },
        {
          id: "bank:private",
          label: "My private question bank",
          prompt: "Create a quiz using my private question bank.",
        },
      ],
    };
  }

  const parsedCreate = parseLocalQuizCreatePrompt(raw);
  if (parsedCreate) return parsedCreate;

  const cat = raw.match(/^cat:(histology|embryology|anatomy)$/i);
  if (cat) {
    const name = cat[1][0].toUpperCase() + cat[1].slice(1).toLowerCase();
    return {
      reply: `Great — I’ll prepare a ${name} quiz from Microlab’s public bank. How many questions and what time limit?`,
      options: [
        {
          id: "preset-10-20",
          label: "10 questions · 20 min",
          prompt: `Create a ${cat[1].toLowerCase()} quiz using 10 medium questions from Microlab's public bank, with a 20-minute time limit.`,
        },
        {
          id: "preset-20-30",
          label: "20 questions · 30 min",
          prompt: `Create a ${cat[1].toLowerCase()} quiz using 20 medium questions from Microlab's public bank, with a 30-minute time limit.`,
        },
        {
          id: "preset-30-45",
          label: "30 questions · 45 min",
          prompt: `Create a ${cat[1].toLowerCase()} quiz using 30 medium questions from Microlab's public bank, with a 45-minute time limit.`,
        },
      ],
    };
  }

  if (/private question bank/.test(t)) {
    return {
      reply:
        "Got it — we’ll use your private question bank. Which topic should I pull questions from? You can also open My Titles to organize topics first.",
      options: [
        { id: "create-quiz", label: "Start over", prompt: "Create a quiz" },
        { id: "ask-quizzes", label: "Help with my question bank", prompt: "Help me with my quizzes and question bank" },
      ],
    };
  }

  // Results/analysis intents are handled live (copilot or getQuizResultsCall) — no create-quiz stub.

  if (/help me with my quizzes|ask about my quizzes|question bank/.test(t)) {
    return {
      reply:
        "I can help with quiz setup, question banks, and results for your authorized classes. What would you like to do?",
      options: [
        { id: "create-quiz", label: "Create a quiz", prompt: "Create a quiz" },
        { id: "check-quiz", label: "Check quiz results", prompt: "Show the results of students in my classes" },
        {
          id: "histology-quiz",
          label: "Quick histology quiz",
          prompt:
            "Create a histology quiz using 20 medium questions from Microlab's public bank, with a 30-minute time limit.",
        },
      ],
    };
  }

  return null;
}
